import { IconCalendarPlus, IconPaperclip } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import { capitalize } from 'lodash';
import classNames from 'classnames';
import { NSBadge } from 'bricks';
import { formatDateShort } from 'ns_libs/formatter';
import { TimelineStatusBootstrapColorMap } from '../../constants';
import UserDisplay from 'components/UserDisplay/UserDisplay';
import { TimelineItem } from '../../types';
import ActivityActionsMenu from '../ActivityActionsMenu/ActivityActionsMenu';

type TimelineActivityRowProps = {
    item: TimelineItem;
    onRowClick: (id: string) => void;
    onAttachmentClick: (id: string) => void;
    onDuplicate: (item: TimelineItem) => void;
    onRequestDelete: (item: TimelineItem) => void;
};

const ReadOnlyDate = ({ value }: { value: string | null }) => (
    value
        ? <span className="text-primary">{formatDateShort(value)}</span>
        : <NSIcon icon={IconCalendarPlus} size="lg" className="text-primary" />
);

const TimelineActivityRow = ({
    item, onRowClick, onAttachmentClick, onDuplicate, onRequestDelete,
}: TimelineActivityRowProps) => {
    const attachmentCount = item.attachmentCount ?? 0;
    const hasAttachments = attachmentCount > 0;

    const handleAttachmentClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (hasAttachments) {
            onAttachmentClick(item.id);
        }
    };

    return (
        <tr
            key={item.id}
            className="timeline-row cursor--pointer"
            tabIndex={0}
            onClick={() => onRowClick(item.id)}
        >
            <td>{item.name}</td>
            <td><ReadOnlyDate value={item.target} /></td>
            <td><ReadOnlyDate value={item.completed} /></td>
            <td>
                <div className="d-flex flex-nowrap assignee-badges-container">
                    {(item.assignees || []).map(assignee => (
                        <div key={assignee.id} className="assignee-badge-readonly">
                            <UserDisplay correlationId={assignee.userCorrelationId} />
                        </div>
                    ))}
                </div>
            </td>
            <td>
                <NSBadge
                    className="ml-1"
                    color={TimelineStatusBootstrapColorMap[item.status] || 'primary-lighten'}
                >
                    {capitalize(item.status)}
                </NSBadge>
            </td>
            <td onClick={handleAttachmentClick}>
                <div
                    className={classNames(
                        'd-flex align-items-center',
                        { 'cursor--pointer': hasAttachments },
                    )}
                >
                    <NSIcon
                        icon={IconPaperclip}
                        className={classNames({
                            'text-muted': !hasAttachments,
                        })}
                    />
                    {hasAttachments && (
                        <span className="ml-1">{attachmentCount}</span>
                    )}
                </div>
            </td>
            <td onClick={e => e.stopPropagation()}>
                <ActivityActionsMenu
                    onDuplicate={() => onDuplicate(item)}
                    onDelete={() => onRequestDelete(item)}
                    dataTestIdPrefix="timeline-row"
                />
            </td>
        </tr>
    );
};

export default TimelineActivityRow;
